import { useState } from "react";
import type { ReactElement } from "react";
import { Check, Facebook, Linkedin, Link2, Mail, Share2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";

// ─── Brand SVGs not accurately represented in lucide-react ───────────────────

/** X (formerly Twitter) — official X logo mark */
function XLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

/** WhatsApp logo mark */
function WhatsAppLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

type IconComponent = LucideIcon | (({ className }: { className?: string }) => ReactElement);

interface ShareLink {
  label: string;
  Icon: IconComponent;
  href: string;
}

interface ShareButtonsProps {
  /** Text used as the post/message body and native share text. Keep it short. */
  text: string;
  /** Optional label above the row, e.g. "Share your result". */
  label?: string;
  className?: string;
  /** Override the shared URL (defaults to the current page). Use this to
   *  share a link that encodes extra state, e.g. a compatibility invite. */
  url?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Plain link-based social sharing. Deliberately avoids embedding any
 * third-party SDK — every button is a share-intent URL that only loads when
 * clicked, adding no extra tracking scripts or cookies. AdSense-safe by
 * construction: no incentivized language, no auto-opening popups, outbound
 * links use rel="noopener noreferrer nofollow", and buttons are styled as
 * plain outlined icons that don't visually resemble ad creative.
 */
export function ShareButtons({ text, label = "Share", className, url: urlOverride }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);
  const url = urlOverride ?? (typeof window !== "undefined" ? window.location.href : "");
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title: text, text, url });
    } catch {
      // User cancelled or platform declined — icon row covers the same ground.
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — silently ignore.
    }
  };

  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(text);

  const links: ShareLink[] = [
    {
      label: "Share on X",
      Icon: XLogo,
      href: `https://x.com/intent/post?text=${encodedText}&url=${encodedUrl}`,
    },
    {
      label: "Share on Facebook",
      Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      label: "Share on LinkedIn",
      Icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
    {
      label: "Share on WhatsApp",
      Icon: WhatsAppLogo,
      href: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    },
    {
      label: "Share via email",
      Icon: Mail,
      href: `mailto:?subject=${encodedText}&body=${encodedUrl}`,
    },
  ];

  const buttonClasses =
    "w-10 h-10 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/40 hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

  return (
    <div className={className}>
      {label && (
        <p className="text-xs uppercase tracking-wide text-muted-foreground font-medium mb-3">{label}</p>
      )}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {canNativeShare && (
          <button
            type="button"
            onClick={handleNativeShare}
            aria-label="Share via device"
            className={buttonClasses}
          >
            <Share2 className="w-4 h-4" />
          </button>
        )}
        {links.map(({ label: linkLabel, Icon, href }) => (
          <a
            key={linkLabel}
            href={href}
            target="_blank"
            rel="noopener noreferrer nofollow"
            aria-label={linkLabel}
            className={buttonClasses}
          >
            <Icon className="w-4 h-4" />
          </a>
        ))}
        <button
          type="button"
          onClick={handleCopy}
          aria-label={copied ? "Link copied" : "Copy link"}
          className={buttonClasses}
        >
          {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
        </button>
        <span role="status" aria-live="polite" className="sr-only">
          {copied ? "Link copied to clipboard" : ""}
        </span>
      </div>
    </div>
  );
}
